// Strings of feature spec 17, phase 6 (GCP networks; spec 11 6.15 items 35–36) in the networking screens.
// `strings.networks.gcp`.
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export const networkGcp = {
  // New network and Settings → Providers
  connectivityProject: 'Connectivity project',
  connectivityProjectHelp: 'The project that holds the network’s private DNS zones and its hub.',
  allowedProjects: 'Allowed projects',
  allowedProjectsLegend: 'Google Cloud projects that may attach',
  connectivityAlwaysAllowed: 'The connectivity project is always allowed.',
  verify: 'Verify',
  verifyProject: (project: string) => `Verify ${project}`,
  // Firewall card (Cloud NGFW)
  firewall: 'Cloud NGFW Standard',
  firewallCost: (asOf: string) => `No fixed price (per-GB processing), list price, as of ${asOf}`,
  natCost: (amount: string) =>
    `+ ${amount}/month for Cloud NAT in each spoke whose apps need outbound traffic`,
  turnedOnForHubSpoke: (amount: string) =>
    `Turned on for hub-spoke; turn it off to save the per-GB processing and ${amount}/month for each spoke that needs Cloud NAT.`,
  firewallOffHelp: 'Off: no Cloud NGFW, no cost.',
  tierLegend: 'Tier',
  tierLabel: { standard: 'Standard', enterprise: 'Enterprise' } as Record<string, string>,
  tierStandardHelp: 'Filters outbound internet traffic by domain name at each attachment’s VPC.',
  egressThrough: 'Through Cloud NGFW',
  egressThroughHelp:
    'Cloud Run sends all its traffic into the VPC; the attachment’s policy lets only allowed host names out, through Cloud NAT where apps need it.',
  egressDirectNeedsOff:
    'On Google Cloud, Cloud NGFW filters only outbound internet traffic; turn the firewall off for direct egress.',
  allowedEgressHelp: 'Exact host names apps may reach, one per row; Cloud NGFW matches no wildcards.',
  egressNotFiltered:
    'Outbound internet traffic from attached deployments on Google Cloud isn’t filtered. Turn on Cloud NGFW with egress through it to filter it.',
  // Topology cards
  nccCost: (amount: string) => `Network Connectivity Center hub · ${amount}/month per attachment`,
  viaHubNotSupported:
    'Google Cloud’s star hub doesn’t carry traffic between spokes: spoke-to-spoke stays blocked.',
  // Overview
  nccHub: 'Network Connectivity Center hub (global)',
  preset: { mesh: 'mesh', star: 'star' } as Record<string, string>,
  ngfwAtEachVpc: 'Cloud NGFW at each VPC',
  phoneHub: (preset: string, firewall: boolean) => `NCC hub (${preset})${firewall ? ' with Cloud NGFW' : ''}`,
  nccHubName: (region: string, preset: string, firewall: boolean) =>
    [
      `Network Connectivity Center hub (global) in ${region}`,
      preset,
      firewall ? 'Cloud NGFW at each VPC' : 'no firewall',
      ...(preset === 'star' ? ['no spoke-to-spoke'] : []),
    ].join(', '),
  grant: (region: string) =>
    `Custom role nbnet_<network>_<deployment>_${region.replace(/-/g, '_')}: firewall rules nb-net-<network>-…, the firewall policy of its VPC and the private DNS binding`,
  hub: 'Hub',
  hubJoined: (deployment: string) => `Joined in ${deployment}’s own apply`,
  hubJoinsNext: 'Joins at its next apply',
  // Private DNS
  cloudDnsZone: 'Cloud DNS private zone',
  peeredVpcs: (n: number) => `Peered with ${plural(n, 'VPC')}`,
  peeringHelp:
    'Each attachment reaches the zone through its own DNS peering zone; connecting needs no network apply.',
  // Traffic tab
  natEgress: 'Google Cloud: through Cloud NAT in each spoke whose apps need it',
  // Deployment side
  attachMoves: (deployment: string, region: string) =>
    `Attaching moves ${deployment}’s Google Cloud resources in ${region} into its own VPC: Cloud Run services get Direct VPC egress (a new revision, same URL) and Cloud SQL instances get a private IP (they restart). Its apply also joins the network’s hub.`,
  // Request targets (item 36)
  sqlNotReachable: 'Private services access isn’t reachable from other VPCs',
  runNeedsIlb: 'Cloud Run needs an internal load balancer',
  // Codes (item 36)
  codes: {
    GCP_FIREWALL_TIER_NOT_AVAILABLE: 'Choose Cloud NGFW Standard; Enterprise comes later.',
    GCP_FIREWALL_NEEDS_EGRESS:
      'On Google Cloud, Cloud NGFW filters outbound internet traffic; turn on egress through it, or turn the firewall off.',
    GCP_EGRESS_WILDCARD_NOT_SUPPORTED:
      'Cloud NGFW matches exact domain names only; list each host name instead.',
    GCP_DEPLOYING_IDENTITY_UNKNOWN:
      'Verify the project so the network knows its deploying identity; its deployments can’t join the hub until then.',
  } as Record<string, string>,
};
